import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Computer,
  Wifi,
  Schedule,
  SignalWifi4Bar,
  SignalWifi3Bar,
  SignalWifi2Bar,
  SignalWifi1Bar,
  SignalWifiOff,
  Router,
  Memory,
  Speed,
  Info,
  Refresh,
  Home,
  Thermostat,
  ScreenshotMonitor,
  Storage,
} from '@mui/icons-material';
import { Device, displayName, isDeviceOnline } from '../types';
import { ChannelChip } from '../devices/ChannelChip';
import { deviceApi, taskApi } from '../api';
import DeviceNameEditor from './DeviceNameEditor';
import TaskList from './TaskList';
import FirmwareUpdate from './FirmwareUpdate';
import StorageLayout from './StorageLayout';
import { formatDateTime, formatRelativeTime, useI18n } from '../i18n';
import { CONTROLLERS_PATH, CONTROLLERS_USB_PATH } from '../nav/paths';

const SCREENSHOT_PATH = '/screenshot.bmp';

/** Turn stored pull payload (`base64,mime,…`) into a data URL. */
function screenshotDataUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  if (content.startsWith('data:')) return content;
  if (content.startsWith('base64,')) {
    const first = content.indexOf(',');
    const second = content.indexOf(',', first + 1);
    if (first < 0 || second < 0) return null;
    const mime = content.slice(first + 1, second) || 'image/bmp';
    const data = content.slice(second + 1);
    return `data:${mime};base64,${data}`;
  }
  return null;
}

const DeviceDetail: React.FC = () => {
  const { t } = useI18n();
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [shotUrl, setShotUrl] = useState<string | null>(null);
  const [shotMeta, setShotMeta] = useState<string | null>(null);
  const [shotBusy, setShotBusy] = useState(false);
  const [shotError, setShotError] = useState<string | null>(null);

  const loadScreenshot = async () => {
    if (!deviceId) return;
    try {
      const stored = await deviceApi.getStoredFile(deviceId, SCREENSHOT_PATH);
      const url = screenshotDataUrl(stored?.content);
      setShotUrl(url);
      setShotMeta(stored
        ? t('detail.screenshotUpdated', { when: formatDateTime(new Date(stored.updatedAt)) })
        : null);
    } catch {
      // ignore — no screenshot yet
    }
  };

  const loadDevice = async () => {
    if (!deviceId) return;
    
    try {
      setError(null);
      const deviceData = await deviceApi.getDevice(deviceId);
      setDevice(deviceData);
      setLastUpdate(new Date());
    } catch (err) {
      setError(t('detail.loadFailed'));
      console.error('Error loading device:', err);
    } finally {
      setLoading(false);
    }
  };

  const captureScreenshot = async () => {
    if (!deviceId) return;
    setShotBusy(true);
    setShotError(null);
    try {
      const capture = await taskApi.createTask(deviceId, { command: 'screenshot' });
      const done = await taskApi.waitForTask(deviceId, capture.id, 120000);
      if (done.state !== 'completed') {
        throw new Error(typeof done.result === 'string' ? done.result : t('detail.screenshotFailed'));
      }
      const pull = await deviceApi.pullStoredFile(deviceId, SCREENSHOT_PATH);
      const pulled = await taskApi.waitForTask(deviceId, pull.id, 240000);
      if (pulled.state !== 'completed') {
        throw new Error(typeof pulled.result === 'string' ? pulled.result : t('detail.screenshotPullFailed'));
      }
      await loadScreenshot();
    } catch (err: any) {
      setShotError(err?.message || t('detail.screenshotFailed'));
    } finally {
      setShotBusy(false);
    }
  };

  useEffect(() => {
    loadDevice();
    loadScreenshot();
    // Auto-refresh every 10 seconds
    const interval = setInterval(loadDevice, 10000);
    return () => clearInterval(interval);
  }, [deviceId]);

  const getSignalIcon = (rssi: number) => {
    if (rssi >= -50) return <SignalWifi4Bar color="success" />;
    if (rssi >= -60) return <SignalWifi3Bar color="success" />;
    if (rssi >= -70) return <SignalWifi2Bar color="warning" />;
    if (rssi >= -80) return <SignalWifi1Bar color="error" />;
    return <SignalWifiOff color="disabled" />;
  };

  const getSignalQuality = (rssi: number) => {
    if (rssi >= -50) return { text: t('signal.excellent'), color: 'success' };
    if (rssi >= -60) return { text: t('signal.good'), color: 'success' };
    if (rssi >= -70) return { text: t('signal.fair'), color: 'warning' };
    return { text: t('signal.poor'), color: 'error' };
  };

  const isOnline = Boolean(device && isDeviceOnline(device));

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, md: 3 }, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('detail.loading')}
        </Typography>
      </Container>
    );
  }

  if (error || !device) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || t('detail.notFound')}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate(CONTROLLERS_PATH)}
        >
          {t('detail.back')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, md: 3 } }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="inherit"
          onClick={() => navigate(CONTROLLERS_PATH)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Home fontSize="small" />
          {t('detail.breadcrumb')}
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Computer fontSize="small" />
          {displayName(device)}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <IconButton onClick={() => navigate(CONTROLLERS_PATH)} color="primary">
                <ArrowBack />
              </IconButton>
              <DeviceNameEditor
                device={device}
                variant="h4"
                onUpdated={setDevice}
              />
              <Chip 
                label={isOnline ? t('common.online') : t('common.offline')} 
                color={isOnline ? 'success' : 'default'}
                size="medium"
              />
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 7, wordBreak: 'break-word' }}>
              {device.model} — {device.firmware || device.version}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={() => navigate(CONTROLLERS_USB_PATH)}
            >
              {t('detail.openUsb')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadDevice}
            >
              {t('common.refresh')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" />
                {t('detail.basic')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Computer />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.name')}
                    secondary={device.hostname || displayName(device)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Computer />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.deviceId')}
                    secondary={device.id || t('common.na')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Memory />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.model')}
                    secondary={device.model}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Speed />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.firmware')}
                    secondary={device.firmware || device.version}
                    secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Router />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.channel')}
                    secondary={<ChannelChip device={device} />}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Router />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.hostname')}
                    secondary={device.hostname}
                  />
                </ListItem>
                {device.mac && (
                  <ListItem>
                    <ListItemIcon>
                      <Wifi />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('common.mac')}
                      secondary={device.mac}
                    />
                  </ListItem>
                )}
                {device.temperature !== undefined && (
                  <ListItem>
                    <ListItemIcon>
                      <Thermostat />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('common.temperature')}
                      secondary={`${device.temperature.toFixed(1)}°C`}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Network Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Wifi color="primary" />
                {t('detail.network')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Router />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.ip')}
                    secondary={device.ip || t('common.na')}
                  />
                </ListItem>
                {device.lqi !== undefined && Number.isFinite(device.lqi) && (
                  <ListItem>
                    <ListItemIcon>
                      <SignalWifi4Bar />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('devices.lqi', { n: device.lqi })}
                      secondary={t('devices.channelZigbee')}
                    />
                  </ListItem>
                )}
                {device.rssi && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        {getSignalIcon(device.rssi)}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('detail.signalStrength')}
                        secondary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {device.rssi} dBm
                            <Chip 
                              label={getSignalQuality(device.rssi).text}
                              size="small"
                              color={getSignalQuality(device.rssi).color as any}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  </>
                )}
                <ListItem>
                  <ListItemIcon>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.lastSeen')}
                    secondary={formatRelativeTime(device.lastSeen, true)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Refresh />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('common.lastUpdated')}
                    secondary={formatDateTime(lastUpdate)}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Flash / partitions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Storage color="primary" />
                {t('detail.storage')}
              </Typography>
              <StorageLayout storage={device.storage} />
            </CardContent>
          </Card>
        </Grid>

        {/* Display screenshot */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScreenshotMonitor color="primary" />
                  {t('detail.screenshot')}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={shotBusy || !isOnline}
                  onClick={captureScreenshot}
                  startIcon={shotBusy ? <CircularProgress size={14} color="inherit" /> : <ScreenshotMonitor />}
                >
                  {shotBusy ? t('detail.screenshotBusy') : t('detail.screenshotCapture')}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {t('detail.screenshotHint')}
              </Typography>
              {shotError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setShotError(null)}>
                  {shotError}
                </Alert>
              )}
              {shotUrl ? (
                <Box
                  component="img"
                  src={shotUrl}
                  alt={t('detail.screenshot')}
                  sx={{
                    width: '100%',
                    maxHeight: 320,
                    objectFit: 'contain',
                    bgcolor: 'grey.900',
                    borderRadius: 1,
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 160,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.screenshotEmpty')}
                  </Typography>
                </Box>
              )}
              {shotMeta && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  {shotMeta}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Firmware Update */}
        <Grid item xs={12} md={6}>
          <FirmwareUpdate deviceId={deviceId!} />
        </Grid>

        {/* Task Management */}
        <Grid item xs={12}>
          <TaskList deviceId={deviceId!} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default DeviceDetail;
